import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { formatUSDT, shortenAddress } from '../../utils/format';
import { Spinner } from '../common/Loader';
import './Team.css';

function gradFor(addr) {
  if (!addr) return 'linear-gradient(135deg, #3B4A45, #1E2A26)';
  const h = parseInt(addr.slice(2, 8), 16) % 360;
  return `linear-gradient(135deg, hsl(${h}, 70%, 55%), hsl(${(h + 60) % 360}, 70%, 45%))`;
}

function initials(addr) {
  if (!addr) return '—';
  return addr.slice(2, 4).toUpperCase();
}

/**
 * Direct-referral list — replaces the old binary tree since the new contract
 * uses a flat sponsor tree with 12-level rewards (no more Left/Right binary).
 */
export default function BinaryTree({ myUserId }) {
  const { read } = useContracts();
  const [directs, setDirects] = useState([]);
  const [directsData, setDirectsData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!myUserId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const ids = await read.storage.getDirects(myUserId);
        if (cancelled) return;
        const list = ids.map((n) => n.toString());
        setDirects(list);

        // Fetch each direct's basic info in parallel
        const results = await Promise.allSettled(
          list.map((id) => read.storage.getUser(id))
        );
        if (cancelled) return;
        const map = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') map[list[i]] = r.value;
        });
        setDirectsData(map);
      } catch {
        if (!cancelled) setDirects([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUserId]);

  return (
    <div className="tree-panel">
      <div className="tree-header">
        <div>
          <h3>Your Direct Referrals</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {directs.length} directs — {Object.values(directsData).filter((u) => u.totalInvestmentUSDT?.gt?.(0)).length} qualifying (have purchased)
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="lg" /></div>
      ) : directs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <div>No direct referrals yet.</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>Share your referral link to grow your team.</div>
        </div>
      ) : (
        <div className="directs-list">
          {directs.map((id) => {
            const u = directsData[id];
            const wallet = u?.wallet;
            const qualifying = u?.totalInvestmentUSDT?.gt?.(0);
            return (
              <div key={id} className="direct-card">
                <div className="direct-avatar" style={{ background: gradFor(wallet) }}>
                  {initials(wallet)}
                </div>
                <div className="direct-info">
                  <div className="d-id">ID {id}</div>
                  <div className="d-addr">{wallet ? shortenAddress(wallet, 4) : '…'}</div>
                  <div className={`d-status ${qualifying ? 'qualifying' : 'pending'}`}>
                    {qualifying ? '✓ Qualifying' : '⌛ Not purchased yet'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
